import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { faPlus, faBook, faXmark, faArrowRightFromBracket, faTrash, faEdit, faFilter } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import DataTable from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import OptionTradeModal from '@/components/journal/OptionTradeModal';
import { APIRoutes } from '@/services/api.routes';
import { JOURNAL_ROUTES } from '@/constants/routes';
import { formatCurrencyCentValue, formatRFCDate, dateToRFCDateValue, toCurrencyCentValue } from '@/utils/helpers';
import { showSuccessToast, showErrorToast } from '@/utils/toasts';
import type { JournalOptionTrade, JournalOptionStatus, JournalStrategy, JournalTag, OptionRight } from '@/models';
import {
  STRATEGY_LABELS,
  OPTION_STATUS_LABELS,
  optionStatusBadgeClass,
  pnlColorClass,
  readableTextColor,
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const PAGE_SIZE = 25;
const SYMBOL_FILTER_DEBOUNCE_MS = 350;

const STATUS_OPTIONS: JournalOptionStatus[] = ['OPEN', 'CLOSED', 'ASSIGNED', 'EXPIRED', 'ROLLED'];
const STRATEGY_OPTIONS: JournalStrategy[] = ['KERNSTRATEGIE', 'REPARATURSTRATEGIE', 'EARNING_TRADE', 'UNKNOWN'];
const RIGHT_OPTIONS: OptionRight[] = ['PUT', 'CALL'];

const fieldClass = `${JOURNAL_FIELD_CLASS} w-full`;

// Query-string values are user editable - anything unknown falls back to "no filter".
function readEnumParam<T extends string>(params: URLSearchParams, key: string, allowed: readonly T[]): T | '' {
  const raw = params.get(key);
  if (raw === null) return '';
  return allowed.includes(raw as T) ? (raw as T) : '';
}

export default function OptionTradesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tagMap, setTagMap] = useState<Record<string, JournalTag>>({});

  const statusFilter = readEnumParam(searchParams, 'status', STATUS_OPTIONS);
  const strategyFilter = readEnumParam(searchParams, 'strategy', STRATEGY_OPTIONS);
  const rightFilter = readEnumParam(searchParams, 'right', RIGHT_OPTIONS);
  const symbolFilter = (searchParams.get('symbol') ?? '').trim().toUpperCase();
  const parsedPage = parseInt(searchParams.get('page') ?? '1', 10);
  const currentPage = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
  const filtersActive = statusFilter !== '' || strategyFilter !== '' || rightFilter !== '' || symbolFilter !== '';

  const [symbolInput, setSymbolInput] = useState(symbolFilter);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<JournalOptionTrade | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JournalOptionTrade | null>(null);
  const [assignTarget, setAssignTarget] = useState<JournalOptionTrade | null>(null);
  const [closeTarget, setCloseTarget] = useState<JournalOptionTrade | null>(null);
  const [closePrice, setClosePrice] = useState('');
  // The price starts empty, so its "required" message stays hidden until the user has typed once.
  const [closePriceTouched, setClosePriceTouched] = useState(false);
  const [closeFee, setCloseFee] = useState('0');
  const [closeDate, setCloseDate] = useState(dateToRFCDateValue(new Date()));
  const [actionBusy, setActionBusy] = useState(false);

  const { data, loading, error, reload } = useJournalResource(
    () => APIRoutes.listJournalOptionTrades({
      limit: PAGE_SIZE,
      next: currentPage > 1 ? `$${currentPage}` : undefined,
      status: statusFilter === '' ? undefined : statusFilter,
      strategy: strategyFilter === '' ? undefined : strategyFilter,
      right: rightFilter === '' ? undefined : rightFilter,
      symbolTicker: symbolFilter === '' ? undefined : symbolFilter,
    }),
    [currentPage, statusFilter, strategyFilter, rightFilter, symbolFilter],
    'Fehler beim Laden der Trades',
  );
  const trades = data?.optionTrades ?? [];
  const total = data?.total ?? 0;

  useEffect(() => {
    APIRoutes.listJournalTags().then((resp) => {
      const map: Record<string, JournalTag> = {};
      for (const tag of resp.tags) map[tag.id] = tag;
      setTagMap(map);
    }).catch((err) => console.error('Failed to load tags:', err));
  }, []);

  const setFilterParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value === '') params.delete(key); else params.set(key, value);
      params.delete('page');
      return params;
    }, { replace: true });
  };

  const setPage = (page: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (page <= 1) params.delete('page'); else params.set('page', page.toString());
      return params;
    }, { replace: true });
  };

  // Deleting the last row of a page would otherwise strand the user on a page that no longer exists.
  useEffect(() => {
    if (loading || error !== null) return;
    const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (currentPage > maxPage) setPage(maxPage);
  }, [total, loading, error, currentPage]);

  // The URL owns the symbol filter; the input catches up when it changes elsewhere (back/forward).
  useEffect(() => { setSymbolInput(symbolFilter); }, [symbolFilter]);

  useEffect(() => {
    if (symbolInput === symbolFilter) return;
    const timer = setTimeout(() => setFilterParam('symbol', symbolInput.trim()), SYMBOL_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [symbolInput, symbolFilter]);

  const openCloseDialog = (trade: JournalOptionTrade) => {
    setCloseTarget(trade);
    setClosePrice('');
    setClosePriceTouched(false);
    setCloseFee('0');
    setCloseDate(dateToRFCDateValue(new Date()));
  };

  const confirmDelete = async () => {
    if (deleteTarget === null) return;
    setActionBusy(true);
    try {
      await APIRoutes.deleteJournalOptionTrade(deleteTarget.id);
      setDeleteTarget(null);
      showSuccessToast('Trade gelöscht');
      reload();
    } catch (err) {
      console.error('Failed to delete trade:', err);
      showErrorToast('Trade konnte nicht gelöscht werden');
    } finally {
      setActionBusy(false);
    }
  };

  const confirmAssign = async () => {
    if (assignTarget === null) return;
    setActionBusy(true);
    try {
      await APIRoutes.assignJournalOptionTrade(assignTarget.id, {});
      setAssignTarget(null);
      showSuccessToast('Zuteilung gebucht');
      reload();
    } catch (err) {
      console.error('Failed to assign trade:', err);
      showErrorToast('Zuteilung konnte nicht gebucht werden');
    } finally {
      setActionBusy(false);
    }
  };

  const closePriceNum = closePrice.trim() === '' ? Number.NaN : Number(closePrice);
  const closeFeeNum = closeFee.trim() === '' ? 0 : Number(closeFee);
  const closePriceError = closePrice.trim() === ''
    ? 'Bitte einen Schließkurs angeben.'
    : Number.isNaN(closePriceNum)
      ? 'Bitte eine gültige Zahl eingeben.'
      : closePriceNum < 0
        ? 'Der Schließkurs darf nicht negativ sein.'
        : null;
  const closeFeeError = Number.isNaN(closeFeeNum)
    ? 'Bitte eine gültige Zahl eingeben.'
    : closeFeeNum < 0
      ? 'Die Gebühr darf nicht negativ sein.'
      : null;
  const closeDateError = closeDate === '' ? 'Bitte ein Schließdatum angeben.' : null;
  const closeValid = closePriceError === null && closeFeeError === null && closeDateError === null;

  // The server recomputes the booked value; this only mirrors its formula for the user.
  const previewPnL = closeTarget === null || !closeValid
    ? null
    : closeTarget.netCredit
      + (closeTarget.direction === 'SELL_TO_OPEN' ? -1 : 1) * toCurrencyCentValue(closePriceNum) * closeTarget.quantity * closeTarget.multiplier
      - toCurrencyCentValue(closeFeeNum);

  const submitClose = async () => {
    if (closeTarget === null || !closeValid) return;
    setActionBusy(true);
    try {
      await APIRoutes.updateJournalOptionTrade(closeTarget.id, {
        status: 'CLOSED',
        closeDate,
        closePrice: toCurrencyCentValue(closePriceNum),
        closeFee: toCurrencyCentValue(closeFeeNum),
      });
      setCloseTarget(null);
      setClosePrice('');
      setCloseFee('0');
      showSuccessToast('Trade geschlossen');
      reload();
    } catch (err) {
      console.error('Failed to close trade:', err);
      showErrorToast('Trade konnte nicht geschlossen werden');
    } finally {
      setActionBusy(false);
    }
  };

  const columns: DataTableColumn<JournalOptionTrade>[] = [
    {
      key: 'symbol',
      header: 'Symbol',
      primary: true,
      render: (t) => (
        <>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-white">{t.symbolTicker}</span>
            {t.mood !== null && <Badge color="gray" title="Stimmung">★{t.mood}</Badge>}
          </div>
          {t.tagIDs.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {t.tagIDs.map((id) => {
                const tag = tagMap[id];
                if (tag === undefined) return null;
                return (
                  <Badge key={id} colorClass="" style={{ backgroundColor: tag.color ?? '#6b7280', color: readableTextColor(tag.color ?? '#6b7280') }}>
                    {tag.name}
                  </Badge>
                );
              })}
            </div>
          )}
        </>
      ),
    },
    { key: 'right', header: 'Typ', render: (t) => t.right },
    { key: 'strike', header: 'Strike', render: (t) => formatCurrencyCentValue(t.strike, t.currency) },
    { key: 'expiry', header: 'Verfall', render: (t) => formatRFCDate(t.expiry) },
    { key: 'quantity', header: 'Menge', align: 'right', render: (t) => t.quantity },
    { key: 'strategy', header: 'Strategie', render: (t) => STRATEGY_LABELS[t.strategy] },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge colorClass={optionStatusBadgeClass(t.status)}>{OPTION_STATUS_LABELS[t.status]}</Badge>,
    },
    { key: 'netCredit', header: 'Netto-Credit', align: 'right', render: (t) => formatCurrencyCentValue(t.netCredit, t.currency) },
    {
      key: 'realizedPnL',
      header: 'Realisiert',
      align: 'right',
      render: (t) => (
        <span className={`font-medium ${pnlColorClass(t.realizedPnL)}`}>
          {t.realizedPnL === null ? '—' : formatCurrencyCentValue(t.realizedPnL, t.currency)}
        </span>
      ),
    },
    {
      key: 'unrealizedPnL',
      header: 'Unrealisiert',
      align: 'right',
      render: (t) => (
        <span className={`font-medium ${pnlColorClass(t.unrealizedPnL)}`}>
          {t.unrealizedPnL === null ? '—' : formatCurrencyCentValue(t.unrealizedPnL, t.currency)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Aktionen',
      align: 'right',
      actions: true,
      render: (t) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" icon={faEdit} title="Bearbeiten" aria-label="Trade bearbeiten" disabled={actionBusy} onClick={() => setEditTarget(t)} />
          {t.status === 'OPEN' && (
            <Button variant="ghost" size="sm" icon={faXmark} title="Schließen" aria-label="Trade schließen" disabled={actionBusy} onClick={() => openCloseDialog(t)} />
          )}
          {t.status === 'OPEN' && t.right === 'PUT' && t.direction === 'SELL_TO_OPEN' && (
            <Button variant="ghost" size="sm" icon={faArrowRightFromBracket} title="Zuteilung buchen" aria-label="Zuteilung buchen" disabled={actionBusy} onClick={() => setAssignTarget(t)} />
          )}
          <Button variant="ghost" size="sm" icon={faTrash} title="Löschen" aria-label="Trade löschen" disabled={actionBusy} onClick={() => setDeleteTarget(t)} />
        </div>
      ),
    },
  ];

  const emptyState = (
    <EmptyState
      icon={faBook}
      title="Keine Trades gefunden"
      description={filtersActive
        ? 'Zu den aktiven Filtern gibt es keine Treffer. Setze die Filter zurück, um alle Trades zu sehen.'
        : 'Du hast noch keinen Options-Trade angelegt. Lege deinen ersten Trade an, um dein Journal zu starten.'}
      action={filtersActive
        ? <Button variant="secondary" onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}>Filter zurücksetzen</Button>
        : <Button icon={faPlus} onClick={() => setCreateOpen(true)}>Neuer Trade</Button>}
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button icon={faPlus} onClick={() => setCreateOpen(true)}>Neuer Trade</Button>
      </div>

      <Card title="Filter" icon={faFilter}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="trade-filter-symbol">Symbol</label>
            <input
              id="trade-filter-symbol"
              className={fieldClass}
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
              placeholder="AAPL"
            />
          </div>
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="trade-filter-status">Status</label>
            <select id="trade-filter-status" className={fieldClass} value={statusFilter} onChange={(e) => setFilterParam('status', e.target.value)}>
              <option value="">Alle Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{OPTION_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="trade-filter-strategy">Strategie</label>
            <select id="trade-filter-strategy" className={fieldClass} value={strategyFilter} onChange={(e) => setFilterParam('strategy', e.target.value)}>
              <option value="">Alle Strategien</option>
              {STRATEGY_OPTIONS.map((s) => (
                <option key={s} value={s}>{STRATEGY_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="trade-filter-right">Typ</label>
            <select id="trade-filter-right" className={fieldClass} value={rightFilter} onChange={(e) => setFilterParam('right', e.target.value)}>
              <option value="">Alle Typen</option>
              {RIGHT_OPTIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error !== null ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <DataTable
          columns={columns}
          rows={trades}
          keyExtractor={(t) => t.id}
          rowHref={(t) => JOURNAL_ROUTES.TRADE_DETAIL(t.id)}
          loading={loading}
          emptyState={emptyState}
        />
      )}

      {error === null && total > PAGE_SIZE && (
        <PageBasedPagination currentPage={currentPage} totalCount={total} pageSize={PAGE_SIZE} onPageChange={setPage} loading={loading} />
      )}

      <OptionTradeModal
        isOpen={createOpen || editTarget !== null}
        editTrade={editTarget}
        onClose={() => { setCreateOpen(false); setEditTarget(null); }}
        onSaved={() => { reload(); }}
      />

      <Modal isOpen={deleteTarget !== null} onClose={() => setDeleteTarget(null)} title="Trade löschen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {deleteTarget !== null && (
              <>
                Der Trade <span className="font-medium text-gray-900 dark:text-white">{deleteTarget.symbolTicker} {deleteTarget.right} {formatCurrencyCentValue(deleteTarget.strike, deleteTarget.currency)} ({formatRFCDate(deleteTarget.expiry)})</span> wird endgültig gelöscht.
              </>
            )}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Das kann nicht rückgängig gemacht werden.</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button variant="danger" loading={actionBusy} onClick={confirmDelete}>Löschen</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={assignTarget !== null} onClose={() => setAssignTarget(null)} title="Zuteilung buchen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          {assignTarget !== null && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Für <span className="font-medium text-gray-900 dark:text-white">{assignTarget.symbolTicker} {assignTarget.right} {formatCurrencyCentValue(assignTarget.strike, assignTarget.currency)} ({formatRFCDate(assignTarget.expiry)})</span> wird die Zuteilung gebucht.
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-300 list-disc pl-5 space-y-1">
                <li>
                  Es entsteht eine Aktienposition über <span className="font-medium text-gray-900 dark:text-white">{assignTarget.quantity * assignTarget.multiplier} Aktien</span> zum Strike von {formatCurrencyCentValue(assignTarget.strike, assignTarget.currency)} (Einstandswert {formatCurrencyCentValue(assignTarget.strike * assignTarget.quantity * assignTarget.multiplier, assignTarget.currency)}).
                </li>
                <li>Der Options-Trade wird mit dem Status „Zugeteilt“ geschlossen.</li>
              </ul>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Diese Buchung kann nicht rückgängig gemacht werden.
              </p>
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setAssignTarget(null)}>Abbrechen</Button>
            <Button variant="danger" loading={actionBusy} onClick={confirmAssign}>Zuteilung buchen</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={closeTarget !== null} onClose={() => setCloseTarget(null)} title="Trade schließen" maxWidth="md" closeDisabled={actionBusy}>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {closeTarget !== null && `${closeTarget.symbolTicker} ${closeTarget.right} ${formatCurrencyCentValue(closeTarget.strike, closeTarget.currency)} zurückkaufen.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="close-price">Schließkurs / Kontrakt ($)</label>
              <input
                id="close-price"
                className={`${fieldClass} ${!closePriceTouched || closePriceError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="number"
                step="0.01"
                value={closePrice}
                onChange={(e) => { setClosePrice(e.target.value); setClosePriceTouched(true); }}
                onBlur={() => setClosePriceTouched(true)}
                placeholder="0.50"
                aria-invalid={closePriceError !== null}
              />
              {closePriceTouched && closePriceError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closePriceError}</p>}
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="close-fee">Gebühr ($)</label>
              <input
                id="close-fee"
                className={`${fieldClass} ${closeFeeError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="number"
                step="0.01"
                value={closeFee}
                onChange={(e) => setCloseFee(e.target.value)}
                aria-invalid={closeFeeError !== null}
              />
              {closeFeeError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closeFeeError}</p>}
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="close-date">Schließdatum</label>
              <input
                id="close-date"
                className={`${fieldClass} ${closeDateError === null ? '' : 'border-red-500 dark:border-red-500'}`}
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                aria-invalid={closeDateError !== null}
              />
              {closeDateError !== null && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{closeDateError}</p>}
            </div>
          </div>
          {closeTarget !== null && previewPnL !== null && (
            <div className="flex items-baseline justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">Voraussichtlich realisiert</span>
              <span className={`text-sm font-semibold ${pnlColorClass(previewPnL)}`}>
                {formatCurrencyCentValue(previewPnL, closeTarget.currency)}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={actionBusy} onClick={() => setCloseTarget(null)}>Abbrechen</Button>
            <Button variant="primary" loading={actionBusy} disabled={!closeValid} onClick={submitClose}>Schließen</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
